import React, { useState, useCallback, useEffect, useRef } from 'react';
import { cloudRead } from '../../services/cloudReadApi';
import SearchableSelect from './SearchableSelect';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, 
  Tooltip, CartesianGrid, Legend 
} from 'recharts';
import { 
  Activity, Cpu, HardDrive, Network, RefreshCw, 
  Cloud, User, Clock, AlertCircle, Search, ShieldCheck, Calendar
} from 'lucide-react';

const CLIENTES_POR_NUBE = {
  aws: [
    { id: 'basa-sis-424329844077', name: 'BASA SIS' },
    { id: 'editorial-esfinge-650912574318', name: 'EDITORIAL ESFINGE' },
    { id: 'estrategias-impulso-prd-337729276858', name: 'ESTRATEGIAS IMPULSO PRD' },
    { id: 'fonacot-prd-257426762527', name: 'FONACOT PRD' },
    { id: 'freyman-log-asociados-639124263804', name: 'FREYMAN LOG ASOCIADOS' },
    { id: 'odessa-app-movil-prd-671036920193', name: 'ODESSA APP MOVIL PRD' },
    { id: 'odessa-app-movil-qa-898845248808', name: 'ODESSA APP MOVIL QA' },
    { id: 'odessa-pyt-714298858363', name: 'ODESSA PYT' },
    { id: 'odessa-seguros-pyt-028433524888', name: 'ODESSA SEGUROS PYT' },
    { id: 'onecore-804450521197', name: 'ONECORE' },
    { id: 'space-aduanas-drp-609437186640', name: 'SPACE ADUANAS DRP' },
    { id: 'sports-world-217533910794', name: 'SPORTS WORLD' }
  ],
  azure: [
    { id: 'pacificglobal', name: 'Pacific Global (DX IA)' },
    { id: 'alica-devqa', name: 'ALICA DEVQA' },
    { id: 'alica-hub', name: 'ALICA HUB' },
    { id: 'alica-nonsap', name: 'ALICA NONSAP' },
    { id: 'alica-prd', name: 'ALICA PRD' },
    { id: 'artigraf', name: 'ARTIGRAF' },
    { id: 'bba', name: 'BB Asociados' },
    { id: 'elementia', name: 'ELEMENTIA' },
    { id: 'fultra-appsfultra', name: 'FULTRA APPSFULTRA' },
    { id: 'fultra-fruehaufinc', name: 'FULTRA FRUEHAUFINC' },
    { id: 'fultra-fruehaufmex', name: 'FULTRA FRUEHAUFMEX' },
    { id: 'fultra-fultra', name: 'FULTRA FULTRA' },
    { id: 'fultra-fultrafabriccsp', name: 'FULTRA FULTRAFABRICCSP' },
    { id: 'fultra-idealeasenoreste1', name: 'FULTRA IDEALEASENORESTE1' },
    { id: 'fultra-idealeasenoreste2', name: 'FULTRA IDEALEASENORESTE2' },
    { id: 'fultra-promare', name: 'FULTRA PROMARE' },
    { id: 'fultra-sierranorte', name: 'FULTRA SIERRANORTE' },
    { id: 'gsoft', name: 'GSOFT' },
    { id: 'imbera', name: 'IMBERA' }
  ],
  gcp: [
    { id: 'flyacross-apps', name: 'FLYACROSS APPS' },
    { id: 'flyacross-sap', name: 'FLYACROSS SAP'},
    { id: 'izzi-dev-dc-sapcore-mx', name: 'IZZI-DEV-DC-SAPCORE-MX' },
    { id: 'izzi-dev-dc-sapeccgtv-mx', name: 'IZZI-DEV-DC-SAPECCGTV-MX'},
    { id: 'izzi-dev-dc-sappo-mx', name: 'IZZI-DEV-DC-SAPPO-MX'},
    { id: 'izzi-dev-dc-saptm-mx', name: 'IZZI-DEV-DC-SAPTM-MX'},
    { id: 'izzi-drp-dc-sapcore-mx', name: 'IZZI-DRP-DC-SAPCORE-MX'},
    { id: 'izzi-drp-dc-sapeccgtv-mx', name: 'IZZI-DRP-DC-SAPECCGTV-MX'},
    { id: 'izzi-fusion-dc-sapcore-mx', name: 'IZZI-FUSION-DC-SAPCORE-MX'},
    { id: 'izzi-prd-dc-sapcore-mx', name: 'IZZI-PRD-DC-SAPCORE-MX'},
    { id: 'izzi-prd-dc-sapeccgtv-mx', name: 'IZZI-PRD-DC-SAPECCGTV-MX'},
    { id: 'izzi-prd-dc-sappo-mx', name: 'IZZI-PRD-DC-SAPPO-MX'},
    { id: 'izzi-prd-dc-saptm-mx', name: 'IZZI-PRD-DC-SAPTM-MX'},
    { id: 'izzi-qas-dc-sapcore-mx', name: 'IZZI-QAS-DC-SAPCORE-MX'},
    { id: 'izzi-qas-dc-sapeccgtv-mx', name: 'IZZI-QAS-DC-SAPECCGTV-MX'},
    { id: 'izzi-qas-dc-saptm-mx', name: 'IZZI-QAS-DC-SAPTM-MX'},
    { id: 'izzi-sha-dc-comms', name: 'IZZI-SHA-DC-COMMS'},
    { id: 'izzi-sha-dc-sapcore-tools', name: 'IZZI-SHA-DC-SAPCORE-TOOLS'},
    { id: 'sportsworld-mx', name: 'SPORTSWORLD-MX'},
    { id: 'sportword-mx-produccion', name: 'SPORTWORD-MX-PRODUCCION'},
    { id: 'sportsworld-com-mx', name: 'SPORTSWORLD-COM-MX'}
  ],
  huawei: [
    { id: 'automatica-mx', name: 'AUTOMATICA' },
    { id: 'club-de-golf-mx', name: 'CLUB DE GOLF' },
    { id: 'fibra-hotelera-mx', name: 'FIBRA HOTELERA' }
  ],
  oci: [{ id: 'OCI-Hegewisch', name: 'Hegewisch' }]
};

export default function MetricasDashboard() {
  const [nube, setNube] = useState('aws');
  const [clientesDisponibles, setClientesDisponibles] = useState(CLIENTES_POR_NUBE['aws']);
  const [cliente, setCliente] = useState(CLIENTES_POR_NUBE['aws'][0]?.id || '');
  
  const [instanceId, setInstanceId] = useState('');
  const [instanciasList, setInstanciasList] = useState([]);
  const [busquedaVm, setBusquedaVm] = useState('');
  const activeRequest = useRef(null);
  const [ociResult, setOciResult] = useState(null);
  useEffect(() => () => activeRequest.current?.abort(), []);
  const vmOptions = instanciasList.map(instance => ({
    id: instance.id,
    label: nube === 'oci'
      ? `${instance.name} (${instance.region}) (Compartment: ${instance.compartment_name})`
      : instance.name,
    search: instance.id,
  }));

  const cancelRequest = () => {
    activeRequest.current?.abort();
    setLoading(false);
    setLoadingInstancias(false);
  };

  const clearMetrics = () => {
    setMetrics(null);
    setOciResult(null);
  };

  const handleBusquedaVm = (value) => {
    setBusquedaVm(value);
    setInstanceId('');
    cancelRequest();
    clearMetrics();
  };

  // Control de Rango de Tiempo
  const [opcionTiempo, setOpcionTiempo] = useState('15');
  const [fechaInicioCustom, setFechaInicioCustom] = useState('');
  const [fechaFinCustom, setFechaFinCustom] = useState('');

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingInstancias, setLoadingInstancias] = useState(false);
  const [error, setError] = useState(null);

  const surfaceClasses = 'border border-violet-500/25 bg-[#1a0d36]/70 backdrop-blur-md shadow-[0_18px_60px_rgba(0,0,0,0.24)] rounded-2xl';
  const fieldClasses = 'bg-black/30 border border-violet-400/30 text-violet-50 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400';

  const handleNubeChange = (nuevaNube) => {
    cancelRequest();
    setNube(nuevaNube);
    const nuevosClientes = CLIENTES_POR_NUBE[nuevaNube] || [];
    setClientesDisponibles(nuevosClientes);
    const primerCliente = nuevosClientes.length > 0 ? nuevosClientes[0].id : '';
    setCliente(primerCliente);
    setInstanciasList([]);
    setBusquedaVm('');
    setInstanceId('');
    clearMetrics();
    if (nuevaNube === 'oci' && opcionTiempo === 'custom') setOpcionTiempo('15');
    setError(null);
  };

  const handleClienteChange = (nuevoCliente) => {
    cancelRequest();
    setCliente(nuevoCliente);
    setInstanciasList([]);
    setBusquedaVm('');
    setInstanceId('');
    clearMetrics();
    setError(null);
  };

  const fetchInstancias = async () => {
    if (!nube || !cliente) return;
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setLoadingInstancias(true);
    setLoading(false);
    setError(null);
    setInstanciasList([]);
    setBusquedaVm('');
    setInstanceId('');
    clearMetrics();

    try {
      if (nube === 'oci') {
        const data = await cloudRead('inventory', {
          reference: cliente, kind: 'machines', include_compartment_names: true,
        }, controller.signal);
        if (controller.signal.aborted) return;
        if (!Array.isArray(data.resources)) throw new Error('Lista de instancias OCI no válida.');
        if (data.resources.some(item => typeof item.compartment_name !== 'string' || !item.compartment_name.trim())) {
          throw new Error('Falta el nombre del compartment de una VM OCI. Revisa el despliegue y los permisos de lectura.');
        }
        setInstanciasList(data.resources);
        if (!data.resources.length) setError('No se encontraron instancias OCI en el alcance configurado para Hegewisch.');
        return;
      }
      const url = `https://healthcenter.kloud-pilot.com/reportes/api/monitoring/instancias-live/?nube=${nube}&cliente=${cliente}`;
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Authorization': 'Bearer dae37acb2e8fdbc1e03a2abf9117749cf3ce8586149d4696cf57262736237de8'
        }
      });
      const data = await response.json();
      if (controller.signal.aborted) return;

      if (response.ok && data.status === 'success') {
        setInstanciasList(data.instancias || []);
        if (data.instancias.length === 0) {
          setError(`No se encontraron instancias encendidas para este cliente en ${nube.toUpperCase()}.`);
        }
      } else {
        setError(data.message || 'Error al obtener lista de instancias.');
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(nube === 'oci' ? err.message : 'Error de conexión al buscar instancias.');
      }
    } finally {
      if (!controller.signal.aborted) setLoadingInstancias(false);
    }
  };

  const calcularMinutosEnvio = useCallback(() => {
    if (opcionTiempo !== 'custom') {
      return Number(opcionTiempo);
    }

    if (!fechaInicioCustom) {
      return 15; // Valor por defecto si no ha seleccionado fecha
    }

    const inicio = new Date(fechaInicioCustom);
    const fin = fechaFinCustom ? new Date(fechaFinCustom) : new Date();

    const diferenciaMs = fin.getTime() - inicio.getTime();
    const minutosCalculados = Math.round(diferenciaMs / (1000 * 60));

    return minutosCalculados > 0 ? minutosCalculados : 15;
  }, [opcionTiempo, fechaInicioCustom, fechaFinCustom]);

  const fetchLiveMetrics = useCallback(async () => {
    if (!nube || !cliente || !instanceId) {
      setError("Por favor selecciona cliente y una máquina válida.");
      return;
    }

    const minutosAConsultar = calcularMinutosEnvio();

    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setLoading(true);
    setError(null);
    setOciResult(null);
    if (nube === 'oci') setMetrics(null);

    try {
      if (nube === 'oci') {
        const data = await cloudRead('metrics', {
          reference: cliente, instance_id: instanceId, minutes: minutosAConsultar,
        }, controller.signal);
        if (controller.signal.aborted) return;
        if (!data.metrics || !data.metric_status) throw new Error('Métricas OCI no válidas.');
        setOciResult(data);
        setMetrics(data.metrics);
        return;
      }
      const url = `https://healthcenter.kloud-pilot.com/reportes/api/monitoring/metricas-live/?nube=${nube}&cliente=${cliente}&minutos=${minutosAConsultar}&instance_id=${instanceId}`;
      
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Authorization': 'Bearer dae37acb2e8fdbc1e03a2abf9117749cf3ce8586149d4696cf57262736237de8' 
        }
      });

      const data = await response.json();
      if (controller.signal.aborted) return;

      if (response.ok && data.status === 'success') {
        setMetrics(data.metrics);
      } else {
        setError(data.message || 'Error al obtener métricas.');
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(nube === 'oci' ? err.message : 'Error de conexión con el servidor backend.');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [nube, cliente, instanceId, calcularMinutosEnvio]);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const minutosConsultados = calcularMinutosEnvio();

    // Si el rango es mayor a 12 horas, incluir la fecha (día/mes) para mejor lectura
    if (minutosConsultados > 720) {
      return `${date.getDate()}/${date.getMonth() + 1} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getNetworkChartData = () => {
    if (!metrics) return [];
    const netIn = metrics.network_in || [];
    const netOut = metrics.network_out || [];

    if (nube === 'oci') {
      const points = new Map();
      [['network_in', 'Entrada'], ['network_out', 'Salida']].forEach(([key, label]) => {
        const status = ociResult?.metric_status[key];
        const interval = /^([1-9]\d*)m$/.exec(status?.interval || '');
        if (status?.status !== 'ok' || status.unit !== 'bytes' || status.aggregation !== 'sum' || !interval) return;
        (metrics[key] || []).forEach(point => {
          if (!points.has(point.timestamp)) points.set(point.timestamp, { time: formatTime(point.timestamp) });
          points.get(point.timestamp)[label] = point.value / (Number(interval[1]) * 60 * 1024);
        });
      });
      return [...points.entries()].sort(([a], [b]) => Date.parse(a) - Date.parse(b)).map(([, point]) => point);
    }

    return netIn.map((item, index) => ({
      time: formatTime(item.timestamp),
      Entrada: (item.value / (60 * 1024)).toFixed(2),
      Salida: (((netOut[index]?.value || 0) / (60 * 1024))).toFixed(2)
    }));
  };

  const getDiskUsageChartData = () => {
    if (!metrics?.disk_usage || metrics.disk_usage.length === 0) return { data: [], paths: [] };

    const paths = metrics.disk_usage.map(d => d.path);
    const timeMap = {};

    metrics.disk_usage.forEach(disk => {
      disk.points.forEach(p => {
        const timeKey = formatTime(p.timestamp);
        if (!timeMap[timeKey]) {
          timeMap[timeKey] = { time: timeKey };
        }
        timeMap[timeKey][disk.path] = Number(p.value).toFixed(2);
      });
    });

    const data = Object.values(timeMap);
    return { data, paths };
  };

  const { data: diskUsageData, paths: diskPaths } = getDiskUsageChartData();
  const DISK_COLORS = ['#f59e0b', '#ec4899', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444'];

  const ociMetricMessage = (key) => {
    if (nube !== 'oci') return null;
    if (!ociResult) return 'Consulta las métricas de la VM para ver los datos.';
    const status = ociResult.metric_status[key];
    if (!status) return 'Esta métrica no está disponible en la integración OCI actual.';
    if (status.status === 'ok') {
      if (key.startsWith('network_') && (status.unit !== 'bytes' || status.aggregation !== 'sum'
        || !/^([1-9]\d*)m$/.test(status.interval || ''))) {
        return 'No se puede convertir el tráfico a KB/s: unidad o intervalo OCI no válido.';
      }
      if (ociResult.metrics[key]?.length > 0) return null;
    }
    return status.message || (status.status === 'error'
      ? 'No se pudo consultar esta métrica OCI.'
      : 'Sin datos en el periodo; revisa el plugin Compute Instance Monitoring.');
  };
  const ociNetworkData = nube === 'oci' ? getNetworkChartData() : [];

  const getDisponibilidadData = () => {
    if (!metrics?.disponibilidad) return [];
    return metrics.disponibilidad.map((item) => ({
      time: formatTime(item.timestamp),
      Salud: nube === 'oci' ? item.value : (item.value === 0 ? 100 : 0)
    }));
  };

  const CustomTooltip = ({ active, payload, label, unit = '%' }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#120823]/95 border border-violet-500/40 p-3 rounded-xl shadow-2xl backdrop-blur-md max-h-52 overflow-y-auto min-w-[200px] z-50">
          <p className="text-xs font-semibold text-violet-300 mb-2 sticky top-0 bg-[#120823] py-1 border-b border-violet-500/30">
            {label}
          </p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="text-xs font-medium flex items-center justify-between gap-3">
                <span className="truncate max-w-[150px]" style={{ color: entry.color }}>
                  {entry.name}:
                </span>
                <span className="font-bold text-white shrink-0">
                  {Number(entry.value).toFixed(2)} {unit}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full space-y-6 p-6">
      
      {/* BARRA SUPERIOR DE SELECTORES */}
      <div className={`${surfaceClasses} relative z-20 p-5`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-300/80">
              Centro de Monitoreo Live
            </p>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2 mt-1">
              <Activity className="h-6 w-6 text-cyan-400 animate-pulse" /> 
              Métricas en Tiempo Real
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-1.5">
              <Cloud className="w-4 h-4 text-violet-400" />
              <select 
                aria-label="Nube de métricas"
                value={nube} 
                onChange={(e) => handleNubeChange(e.target.value)} 
                className={fieldClasses}
              >
                <option value="aws">AWS</option>
                <option value="azure">Azure</option>
                <option value="gcp">GCP</option>
                <option value="huawei">Huawei</option>
                <option value="oci">OCI</option>
              </select>
            </div>

            <div className="flex items-center space-x-1.5">
              <User className="w-4 h-4 text-violet-400" />
              <select 
                aria-label="Cliente de métricas"
                value={cliente} 
                onChange={(e) => handleClienteChange(e.target.value)} 
                className={`${fieldClasses} w-52`}
                disabled={clientesDisponibles.length === 0}
              >
                {clientesDisponibles.length === 0 ? (
                  <option value="">-- Sin clientes --</option>
                ) : (
                  clientesDisponibles.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <button 
              onClick={fetchInstancias}
              disabled={loadingInstancias || !cliente}
              className="inline-flex items-center gap-1.5 rounded-xl border border-cyan-400/40 bg-cyan-500/20 px-3 py-2 text-xs font-semibold text-cyan-200 transition-all hover:bg-cyan-500/35 disabled:opacity-50"
            >
              <Search className={`h-3.5 w-3.5 ${loadingInstancias ? 'animate-spin' : ''}`} />
              {loadingInstancias ? 'Buscando...' : `Consultar VMs`}
            </button>

            <SearchableSelect label="Máquina virtual" options={vmOptions} value={instanceId}
              text={vmOptions.find(option => option.id === instanceId)?.label || busquedaVm}
              onTextChange={handleBusquedaVm}
              onSelect={option => {
                cancelRequest(); setInstanceId(option.id); setBusquedaVm(''); clearMetrics();
              }}
              placeholder={instanciasList.length ? 'Buscar VM por nombre o ID...' : 'Primero consulta las VMs del cliente'}
              disabled={loadingInstancias || instanciasList.length === 0}
              className={`${fieldClasses} w-full sm:w-96`} />

            {/* SELECTOR DE TIEMPO AMPLIADO */}
            <div className="flex items-center space-x-1.5">
              <Clock className="w-4 h-4 text-violet-400" />
              <select 
                aria-label="Rango de métricas"
                value={opcionTiempo} 
                onChange={(e) => {
                  cancelRequest();
                  setOpcionTiempo(e.target.value);
                  if (nube === 'oci') clearMetrics();
                }}
                className={fieldClasses}
              >
                <option value="15">Últimos 15 min</option>
                <option value="30">Últimos 30 min</option>
                <option value="60">Última 1 hora</option>
                <option value="120">Últimas 2 horas</option>
                <option value="300">Últimas 5 horas</option>
                <option value="720">Últimas 12 horas</option>
                <option value="1440">Últimas 24 horas</option>
                {nube !== 'oci' && <option value="custom">📅 Personalizado...</option>}
              </select>
            </div>

            {/* INPUTS DE FECHA/HORA CUSTOM */}
            {opcionTiempo === 'custom' && (
              <div className="flex items-center gap-2 bg-violet-950/40 p-1.5 rounded-xl border border-violet-400/30">
                <Calendar className="w-4 h-4 text-cyan-400 shrink-0 ml-1" />
                <input 
                  type="datetime-local" 
                  value={fechaInicioCustom} 
                  onChange={(e) => setFechaInicioCustom(e.target.value)} 
                  className="bg-black/40 border border-violet-400/20 text-violet-100 text-xs rounded-lg px-2 py-1 focus:outline-none"
                  title="Fecha y hora de inicio"
                />
                <span className="text-xs text-violet-300 font-semibold">a</span>
                <input 
                  type="datetime-local" 
                  value={fechaFinCustom} 
                  onChange={(e) => setFechaFinCustom(e.target.value)} 
                  className="bg-black/40 border border-violet-400/20 text-violet-100 text-xs rounded-lg px-2 py-1 focus:outline-none"
                  title="Fecha y hora fin (opcional, por defecto AHORA)"
                />
              </div>
            )}

            <button 
              onClick={fetchLiveMetrics} 
              disabled={loading || !instanceId}
              className="inline-flex items-center gap-2 rounded-xl border border-violet-400/40 bg-violet-500/20 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-violet-500/35 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Consultar
            </button>
          </div>

        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {nube === 'oci' && ociResult && <p className="text-xs text-violet-200">
        Consultado: {ociResult.queried_at}
        {ociResult.partial && ' · Consulta parcial: revisa los errores por métrica'}
      </p>}
      {nube === 'oci' && ociResult && ['disk_read', 'disk_write'].map(key =>
        ociResult.metric_status[key]?.status === 'error' && <p key={key} className="text-sm text-red-300">
          {key === 'disk_read' ? 'Lectura de disco' : 'Escritura de disco'}: {ociResult.metric_status[key].message
            || 'No se pudo consultar esta métrica OCI.'}
        </p>
      )}

      {/* GRID DE GRÁFICAS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 1. CPU */}
        <div className={`${surfaceClasses} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-violet-100 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-violet-400" /> Uso de CPU (%)
            </h3>
            <span className="text-xs text-violet-300/60 font-mono">{nube.toUpperCase()}</span>
          </div>
          <div className="h-64 w-full">
            {ociMetricMessage('cpu') ? <div className="h-full flex items-center justify-center text-sm text-violet-200">
              {ociMetricMessage('cpu')}
            </div> :
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={metrics?.cpu?.map(d => ({ time: formatTime(d.timestamp), CPU: d.value })) || []}>
                <defs>
                  <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2e1065" opacity={0.5} />
                <XAxis dataKey="time" stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} />
                <YAxis stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} domain={[0, 'auto']} />
                <Tooltip content={<CustomTooltip unit="%" />} />
                <Area type="monotone" dataKey="CPU" stroke="#a78bfa" strokeWidth={2} fillOpacity={1} fill="url(#cpuGradient)" />
              </AreaChart>
            </ResponsiveContainer>}
          </div>
        </div>

        {/* 2. RAM */}
        <div className={`${surfaceClasses} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-violet-100 flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-cyan-400" /> Uso de Memoria RAM (%)
            </h3>
            <span className="text-xs text-cyan-300/60 font-mono">Agente</span>
          </div>
          <div className="h-64 w-full">
            {!ociMetricMessage('memory') && metrics?.memory?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={metrics.memory.map(d => ({ time: formatTime(d.timestamp), RAM: d.value }))}>
                  <defs>
                    <linearGradient id="ramGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2e1065" opacity={0.5} />
                  <XAxis dataKey="time" stroke="#22d3ee" tick={{ fill: '#22d3ee', fontSize: 11 }} />
                  <YAxis stroke="#22d3ee" tick={{ fill: '#22d3ee', fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip unit="%" />} />
                  <Area type="monotone" dataKey="RAM" stroke="#38bdf8" strokeWidth={2} fillOpacity={1} fill="url(#ramGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-violet-300/50 italic">
                {ociMetricMessage('memory') || 'Sin datos de agente de memoria en esta VM'}
              </div>
            )}
          </div>
        </div>

        {/* 3. RED */}
        <div className={`${surfaceClasses} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-violet-100 flex items-center gap-2">
              <Network className="w-5 h-5 text-emerald-400" /> Tráfico de Red (KB/s)
            </h3>
            <span className="text-xs text-emerald-300/60 font-mono">In / Out</span>
          </div>
          {nube === 'oci' && ['network_in', 'network_out'].map((key, index) => ociMetricMessage(key) && (
            <p key={key} className="text-sm text-violet-200">
              {index === 0 ? 'Entrada' : 'Salida'}: {ociMetricMessage(key)}
            </p>
          ))}
          <div className="h-64 w-full">
            {nube === 'oci' && !ociNetworkData.length
              ? <div className="h-full flex items-center justify-center text-sm text-violet-200">Sin datos de tráfico para graficar.</div>
              :
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={nube === 'oci' ? ociNetworkData : getNetworkChartData()}>
                <defs>
                  <linearGradient id="netInGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="netOutGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2e1065" opacity={0.5} />
                <XAxis dataKey="time" stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} />
                <YAxis stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip unit="KB/s" />} />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                <Area type="monotone" dataKey="Entrada" stroke="#34d399" strokeWidth={2} fillOpacity={1} fill="url(#netInGradient)" />
                <Area type="monotone" dataKey="Salida" stroke="#60a5fa" strokeWidth={2} fillOpacity={1} fill="url(#netOutGradient)" />
              </AreaChart>
            </ResponsiveContainer>}
          </div>
        </div>

        {/* 4. DISCO - USO DE ESPACIO (%) */}
        <div className={`${surfaceClasses} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-violet-100 flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-amber-400" /> Uso de Disco por Partición (%)
            </h3>
            <span className="text-xs text-amber-300/60 font-mono">
              {diskPaths.length > 0 ? `${diskPaths.length} particiones` : 'Agente'}
            </span>
          </div>
          <div className="h-64 w-full">
            {!ociMetricMessage('disk_usage') && diskPaths.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={diskUsageData}>
                  <defs>
                    {diskPaths.map((path, idx) => (
                      <linearGradient key={path} id={`diskGrad_${idx}`} x1="0" y1="0" x2="0" y2="1">
                        <stop 
                          offset="5%" 
                          stopColor={DISK_COLORS[idx % DISK_COLORS.length]} 
                          stopOpacity={diskPaths.length > 5 ? 0.2 : 0.6}
                        />
                        <stop 
                          offset="95%" 
                          stopColor={DISK_COLORS[idx % DISK_COLORS.length]} 
                          stopOpacity={0}
                        />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2e1065" opacity={0.5} />
                  <XAxis dataKey="time" stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} />
                  <YAxis stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip unit="%" />} />
                  <Legend 
                    wrapperStyle={{ 
                      fontSize: '11px', 
                      paddingTop: '6px', 
                      maxHeight: '42px', 
                      overflowY: 'auto' 
                    }} 
                  />
                  {diskPaths.map((path, idx) => (
                    <Area 
                      key={path} 
                      type="monotone" 
                      dataKey={path} 
                      stroke={DISK_COLORS[idx % DISK_COLORS.length]} 
                      strokeWidth={diskPaths.length > 8 ? 1.5 : 2} 
                      fillOpacity={1} 
                      fill={`url(#diskGrad_${idx})`} 
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-violet-300/50 italic">
                {nube === 'oci'
                  ? ociMetricMessage('disk_usage')
                  : 'Sin datos de agente de disco en esta VM'}
              </div>
            )}
          </div>
        </div>

        {/* 5. DISPONIBILIDAD / ESTADO DE SALUD */}
        <div className={`${surfaceClasses} p-5 lg:col-span-2`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-violet-100 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" /> Disponibilidad y Estado de Salud (%)
            </h3>
            <span className="text-xs text-emerald-300/60 font-mono">{nube === 'oci' ? 'instance_status' : 'StatusCheckFailed'}</span>
          </div>
          <div className="h-48 w-full">
            {ociMetricMessage('disponibilidad') ? <div className="h-full flex items-center justify-center text-sm text-violet-200">
              {ociMetricMessage('disponibilidad')}
            </div> :
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={getDisponibilidadData()}>
                <defs>
                  <linearGradient id="saludGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2e1065" opacity={0.5} />
                <XAxis dataKey="time" stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} />
                <YAxis stroke="#a78bfa" tick={{ fill: '#a78bfa', fontSize: 11 }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip unit="%" />} />
                <Area type="stepAfter" dataKey="Salud" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#saludGrad)" />
              </AreaChart>
            </ResponsiveContainer>}
          </div>
          {nube === 'oci' && ociResult?.metric_status.disponibilidad?.status === 'ok' && (
            <p className="text-xs text-violet-200 mt-2">
              Disponibilidad de infraestructura según instance_status; no mide salud de aplicaciones ni un SLA.
              Los periodos sin datos no se consideran disponibles.
            </p>
          )}
        </div>

      </div>

    </div>
  );
}